/**
 * BulkRemindPanel — cobrança em massa via WhatsApp com salvaguardas
 * anti-ban (throttle, cap diário, horário comercial, anti-repeat 48h,
 * circuit breaker). A UI é agnóstica de fluxo: passa-se o par de
 * endpoints (`previewUrl` + `fireUrl`) e um label pro documento.
 *
 * Todas as regras anti-ban vivem no backend (bulk_reminders.py). Aqui
 * a UI só desenha o botão, preview, confirmação e polling do progresso.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Loader2 } from "lucide-react";
import { API } from "../App";

export function BulkRemindPanel({
  previewUrl,        // e.g. `/admin/vacation-notices/xxx/bulk-remind/preview`
  fireUrl,           // e.g. `/admin/vacation-notices/xxx/bulk-remind`
  hasPending,        // boolean — hide panel when the batch is fully signed
  docLabel = "documento pendente",
  onDone,
}) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runData, setRunData] = useState(null);
  const pollRef = useRef(null);

  const openConfirm = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await axios.get(`${API}${previewUrl}`);
      setPreview(r.data);
      setConfirmOpen(true);
    } catch {
      toast.error("Erro ao carregar prévia de cobrança");
    } finally {
      setLoadingPreview(false);
    }
  };

  const fire = async () => {
    try {
      const r = await axios.post(`${API}${fireUrl}`);
      if (!r.data.started) {
        toast.warning(
          r.data.reason === "out_of_business_hours"
            ? "Fora do horário comercial (09h–17h · seg–sex)."
            : "Nenhum elegível.",
        );
        setConfirmOpen(false);
        return;
      }
      setRunId(r.data.run_id);
      setConfirmOpen(false);
      toast.success(
        `Cobrança iniciada — ${r.data.eligible} funcionário(s) na fila.`,
      );
    } catch {
      toast.error("Erro ao disparar cobrança");
    }
  };

  // Polling — every 3 s while the run is not finished/aborted.
  useEffect(() => {
    if (!runId) return undefined;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/admin/bulk-reminders/${runId}`);
        setRunData(r.data);
        const st = r.data?.run?.status;
        if (st === "done" || st === "aborted") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (onDone) onDone();
        }
      } catch {
        /* transient — keep polling */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [runId, onDone]);

  if (!hasPending && !runData) return null;

  const run = runData?.run;
  const sends = runData?.sends || [];
  const progress = run
    ? Math.round(((run.sent + run.failed) / (run.planned || 1)) * 100)
    : 0;

  return (
    <div
      className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-3"
      data-testid="bulk-remind-panel"
    >
      {!run && (
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-800">
              Cobrar pendentes por WhatsApp
            </p>
            <p className="text-xs text-slate-600">
              Dispara uma mensagem personalizada para cada funcionário com{" "}
              {docLabel}, com throttling anti-bloqueio (15s entre envios,
              cap 40/dia, horário comercial).
            </p>
          </div>
          <Button
            size="sm"
            onClick={openConfirm}
            disabled={loadingPreview}
            data-testid="bulk-remind-open-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
          >
            {loadingPreview ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Cobrar pendentes"
            )}
          </Button>
        </div>
      )}

      {run && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm font-semibold text-emerald-800">
              {run.status === "done"
                ? "Cobrança concluída"
                : run.status === "aborted"
                ? "Cobrança interrompida"
                : "Cobrando pendentes…"}
            </p>
            <span className="text-xs font-mono text-slate-600">
              {run.sent + run.failed}/{run.planned}
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden mb-2">
            <div
              className={
                run.status === "aborted" ? "bg-amber-500" : "bg-emerald-500"
              }
              style={{
                width: `${progress}%`,
                height: "100%",
                transition: "width .4s",
              }}
            />
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span>✅ {run.sent} enviado(s)</span>
            {run.failed > 0 && (
              <span className="text-red-600">⚠️ {run.failed} falha(s)</span>
            )}
            {run.status === "aborted" && (
              <span className="text-amber-700 font-semibold">
                (fora do horário ou circuit breaker)
              </span>
            )}
          </div>
          {sends.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-slate-500 cursor-pointer">
                Ver log de envios
              </summary>
              <ul className="mt-1.5 space-y-0.5 text-[11px] font-mono max-h-40 overflow-y-auto">
                {sends.map((s, i) => (
                  <li
                    key={i}
                    className={
                      s.success ? "text-emerald-700" : "text-red-600"
                    }
                  >
                    {s.success ? "✓" : "✗"} {s.nome} · {s.whatsapp}
                    {!s.success && s.error && ` · ${s.error.slice(0, 60)}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirmar cobrança em massa</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-emerald-700">
                  {preview.eligible}
                </span>
                <span className="text-slate-600">
                  funcionário(s) receberão a cobrança
                </span>
              </div>
              {preview.blocked > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs">
                  <p className="font-semibold text-amber-800 mb-1">
                    {preview.blocked} bloqueado(s):
                  </p>
                  <ul className="space-y-0.5 text-amber-700">
                    {preview.blocked_breakdown.no_whatsapp && (
                      <li>
                        • {preview.blocked_breakdown.no_whatsapp} sem WhatsApp
                        confirmado
                      </li>
                    )}
                    {preview.blocked_breakdown.recent_reminder && (
                      <li>
                        • {preview.blocked_breakdown.recent_reminder} já
                        receberam cobrança nas últimas 48h
                      </li>
                    )}
                    {preview.blocked_breakdown.daily_cap && (
                      <li>
                        • {preview.blocked_breakdown.daily_cap} passariam do
                        cap diário
                      </li>
                    )}
                    {preview.blocked_breakdown.signed && (
                      <li>
                        • {preview.blocked_breakdown.signed} já assinaram
                      </li>
                    )}
                  </ul>
                </div>
              )}
              <div className="text-xs text-slate-600 space-y-1">
                <p>
                  Enviadas hoje:{" "}
                  <strong>
                    {preview.cap_used_today}/{preview.cap_total}
                  </strong>{" "}
                  · Horário: {preview.business_hours_label}
                </p>
                {!preview.in_business_hours && (
                  <p className="text-red-600 font-semibold">
                    ⚠️ Fora do horário comercial — dispare novamente entre
                    09h e 17h de segunda a sexta.
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={fire}
              disabled={
                !preview ||
                preview.eligible === 0 ||
                !preview.in_business_hours
              }
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-remind-confirm-btn"
            >
              Disparar agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BulkRemindPanel;
